import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

export interface SegmentedOption {
  label: string;
  value: string;
  /**
   * A second line under the label, for a switch whose options need saying what they DO.
   *
   * On a disabled option this is where the reason goes — the same rule the row menu follows: an
   * unavailable choice stays visible WITH its reason rather than being greyed and unexplained. A tooltip
   * alone is not enough for that, because nothing tells the reader there is one to hover for.
   */
  sublabel?: string;
  /** Optional count rendered after the label (e.g. number of changed items). */
  badge?: number | null;
  /**
   * One option out of the set that can't be picked right now, with the rest still live. Distinct from
   * `SegmentedConfig.disabled`, which stands the whole switch down.
   */
  disabled?: boolean;
  /**
   * Tooltip for this option — the place to say WHY a disabled one can't be picked. It opens on a disabled
   * option too: they are only `aria-disabled`, precisely so the reason stays reachable.
   */
  title?: string;
}

/** Control height/type scale. `md` is the default and matches the app's standard filter chips. */
export type SegmentedSize = 'sm' | 'md' | 'lg';

export interface SegmentedConfig {
  options: SegmentedOption[];
  disabled?: boolean;
  /**
   * Fill the container, with the options sharing it equally.
   *
   * For a switch that IS the choice on a form — the ticket type, say — rather than a filter sitting beside
   * other controls. Content-width options give the widest label the most room, which reads as though it
   * matters most; equal thirds say the three are alternatives of the same standing.
   */
  fullWidth?: boolean;
  /** Default `md`. Use `lg` when the switch is a primary page control rather than a compact filter. */
  size?: SegmentedSize;
}

/**
 * Pill-style segmented switch for an exclusive choice — a view filter (All / Granted / Changed), or a form
 * value where the options are few enough to show at once and a radio list would cost more room than it
 * earns. It is not a ControlValueAccessor: bind `[value]` / `(valueChange)` to the control for the latter.
 */
@Component({
  selector: 'app-segmented',
  standalone: true,
  imports: [NgbTooltip],
  template: `
    <div
      class="segmented"
      role="group"
      [class]="'segmented--' + (config.size ?? 'md')"
      [class.is-disabled]="config.disabled"
      [class.is-full]="config.fullWidth"
    >
      @for (option of config.options; track option.value) {
        <button
          type="button"
          class="segmented-option"
          [class.is-active]="option.value === value"
          [class.is-disabled]="config.disabled || option.disabled"
          [attr.aria-disabled]="config.disabled || option.disabled ? true : null"
          [ngbTooltip]="option.title || null"
          [attr.aria-pressed]="option.value === value"
          (click)="select(option.value)"
        >
          <span class="segmented-label">{{ option.label }}</span>
          @if (option.sublabel) {
            <span class="segmented-sublabel">{{ option.sublabel }}</span>
          }
          @if (option.badge !== null && option.badge !== undefined) {
            <span class="segmented-badge">{{ option.badge }}</span>
          }
        </button>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
      }

      .segmented {
        display: inline-flex;
        gap: 2px;
        padding: 3px;
        border-radius: 10px;
        background: var(--bg-subtle, #f3f4f6);

        &.is-disabled {
          opacity: 0.6;
        }

        /* Equal shares, centred. Content-width options hand the widest label the most room, which reads
           as though it matters most — equal thirds say the options are alternatives of one standing. */
        &.is-full {
          display: flex;
          width: 100%;

          .segmented-option {
            flex: 1 1 0;
            min-width: 0;
            justify-content: center;
            text-align: center;
          }

          /* The two-line variant stacks left by default; filling the width, it centres with the rest. */
          .segmented-option:has(.segmented-sublabel) {
            align-items: center;
          }
        }
      }

      /* Only the scale differs between sizes — colours and states are shared above/below. */
      .segmented--sm .segmented-option {
        padding: 4px 10px;
        font-size: 0.6875rem;
      }

      .segmented--lg {
        padding: 4px;
        border-radius: 12px;
      }

      .segmented--lg .segmented-option {
        padding: 8px 16px;
        border-radius: 9px;
        font-size: 0.875rem;
      }

      .segmented-option {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 5px 12px;
        border: 0;
        border-radius: 8px;
        background: transparent;
        font: inherit;
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--fg-2, #64748b);
        cursor: pointer;
        white-space: nowrap;
        transition:
          background 0.15s ease,
          color 0.15s ease,
          box-shadow 0.15s ease;

        /* is-disabled on the OPTION rather than the :disabled attribute — see the template for why. The
           container carries the same class name one level up, so this compiles to
           .segmented-option.is-disabled and the two never collide. */
        &.is-disabled {
          cursor: not-allowed;
          color: var(--fg-4, #94a3b8);
          /* Dimmed as well as recoloured: an option has to read as unavailable before its reason is
             read. A greyer label alone is easy to mistake for the inactive-but-pickable state. */
          opacity: 0.45;
        }

        /* A click does nothing here, so nothing should invite one. */
        &.is-disabled:hover,
        &.is-disabled:active {
          background: transparent;
          transform: none;
        }

        /* One option down while the rest are live: the fade has to sit on the option, since the whole
           control's own is-disabled opacity would take the live ones with it. */
        .segmented:not(.is-disabled) &.is-disabled {
          opacity: 0.55;
        }

        &.is-active {
          background: var(--bg-surface, #fff);
          color: var(--h4c-dark-blue, #262262);
          box-shadow: 0 1px 3px rgba(38, 34, 98, 0.14);
        }

        &:focus-visible {
          outline: 2px solid var(--h4c-bright-blue, #27aae1);
          outline-offset: 1px;
        }
      }

      .segmented-option:has(.segmented-sublabel) {
        flex-direction: column;
        align-items: flex-start;
        gap: 1px;
        padding-top: 6px;
        padding-bottom: 6px;
      }

      .segmented-sublabel {
        font-size: 9.5px;
        font-weight: 500;
        line-height: 1.25;
        opacity: 0.72;
      }

      .segmented-badge {
        font-variant-numeric: tabular-nums;
        opacity: 0.7;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegmentedComponent implements OnInit {
  @Input() config: SegmentedConfig = { options: [] };
  @Input() value: string | null = null;
  /**
   * Mirror the selection in the URL, so a reload, a Back, or a pasted link lands on the same segment — the
   * same contract as app-horizontal-tabs' own `statePersist`. Off by default: a switch is only worth a query
   * param when it changes what the page SHOWS, and a page with two of them would otherwise have them fight
   * over one key.
   */
  @Input() statePersist = false;
  /**
   * The param to keep it in. Named per switch rather than defaulting to `tab` (which app-horizontal-tabs
   * owns), so a page can carry tabs and a switch without one overwriting the other.
   */
  @Input() queryParamKey = 'view';
  @Output() valueChange = new EventEmitter<string>();

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /**
   * The component does not hold the value — the host does — so restoring means EMITTING what the URL asked
   * for and letting the host apply it, exactly as a click would. Same reason the tabs component emits its
   * restored id instead of just setting it.
   *
   * A host that reads the param itself (to have it in hand before its own first fetch) leaves nothing to do
   * here: the values already agree and the emit is skipped.
   */
  ngOnInit(): void {
    if (!this.statePersist) return;
    const fromRoute = this.route.snapshot.queryParamMap.get(this.queryParamKey);
    const restored = this.config.options.find((o) => o.value === fromRoute && !o.disabled);
    if (restored && restored.value !== this.value) {
      // Deferred: emitting into the host mid-construction would write to its state during its own first
      // change detection.
      queueMicrotask(() => this.valueChange.emit(restored.value));
      return;
    }
    // Nothing usable in the URL — put what IS showing there, so the link is shareable from the first paint.
    if (this.value) this.persist(this.value);
  }

  select(value: string): void {
    if (this.config.disabled || value === this.value) return;
    // THE guard, not a backstop: a disabled option is only `aria-disabled`, so its click still arrives here
    // — which is exactly what keeps its tooltip reachable — and this is what refuses to act on it.
    if (this.config.options.find((o) => o.value === value)?.disabled) return;
    this.valueChange.emit(value);
    this.persist(value);
  }

  /** `merge` so the switch owns its own key and leaves every other param alone; `replaceUrl` so flicking
   *  through segments doesn't fill the Back button with steps nobody wants to walk back through. */
  private persist(value: string): void {
    if (!this.statePersist) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.queryParamKey]: value },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
